import { useSectionAccess } from "../../../auth/useSectionAccess";
import { ChangeEvent, FormEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { attributeLabel, optionLabel } from "./attributeSchema";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  catalogApi,
  type AssetKind,
  type AssetView,
  type AttributeSchemaEntry,
  type CategoryNode,
  type ProductStatus,
  type ProductTranslation,
} from "../../../api/catalog";
import type { ApiError } from "../../../api/client";
import { useConfirm } from "../../../components/ConfirmDialog";
import { Select } from "../../../components/Select";
import { catalogLocale } from "../../../lib/localizedName";

const LOCALES = ["sr", "en", "de", "hr", "sl", "it", "es", "fr", "ru", "tr"] as const;
const ASSET_KINDS: AssetKind[] = ["IMAGE", "DATASHEET_PDF", "SPECIFICATION", "COA", "CERTIFICATE", "DECLARATION"];

export default function AdminProductEditorPage() {
  const { t, i18n } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === "new";
  const navigate = useNavigate();

  const [categories, setCategories] = useState<CategoryNode[]>([]);
  const [tab, setTab] = useState<"general" | "translations" | "attributes" | "assets">("general");
  const [error, setError] = useState<string | null>(null);
  const [savingMsg, setSavingMsg] = useState<string | null>(null);

  // Form state
  const [sku, setSku] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [status, setStatus] = useState<ProductStatus>("DRAFT");
  const [attributes, setAttributes] = useState<Record<string, unknown>>({});
  const [translations, setTranslations] = useState<Record<string, ProductTranslation>>({
    en: { name: "", shortDescription: null, longDescription: null },
  });
  const [assets, setAssets] = useState<AssetView[]>([]);
  const [productId, setProductId] = useState<string | null>(null);
  const [schema, setSchema] = useState<AttributeSchemaEntry[]>([]);
  const [createdAt, setCreatedAt] = useState<string | null>(null);
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  // The status and category of an article from the ERP follow the ERP (the server refuses another
  // one), so the form shows them without offering a choice and never sends them.
  const [fromErp, setFromErp] = useState(false);

  useEffect(() => {
    void catalogApi.tree(i18n.resolvedLanguage ?? "en").then(setCategories).catch(() => undefined);
  }, []);

  // Fetch attribute schema whenever the chosen category changes (separate
  // call because the public tree endpoint omits attribute_schema).
  useEffect(() => {
    if (!categoryId) {
      setSchema([]);
      return;
    }
    void catalogApi.getCategory(categoryId).then((c) => setSchema(c.attributeSchema ?? [])).catch(() => setSchema([]));
  }, [categoryId]);

  useEffect(() => {
    if (isNew || !id) return;
    void catalogApi.getProduct(id).then((p) => {
      setProductId(p.id);
      setSku(p.sku);
      setCategoryId(p.categoryId ?? "");
      setStatus(p.status);
      setAttributes(p.attributes ?? {});
      setTranslations(p.translations ?? {});
      setAssets(p.assets ?? []);
      setCreatedAt(p.createdAt ?? null);
      setUpdatedAt(p.updatedAt ?? null);
      setFromErp(!!p.fromErp);
    }).catch((e: ApiError) => setError(e.message));
  }, [id, isNew]);

  // Name shown on the General tab: current UI language, then English, then Serbian.
  const shownLocale = catalogLocale(translations, i18n.resolvedLanguage ?? "en");
  const nameForLocale = (shownLocale && translations[shownLocale]?.name?.trim()) || "";

  async function handleSave(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSavingMsg(null);
    // Select is a <button>, so it carries no native `required` — the category
    // has to be checked here or an empty one reaches the API.
    if (!categoryId) {
      setError(t("admin.productEditor.categoryRequired"));
      return;
    }
    try {
      if (isNew) {
        if (!translations.en?.name?.trim()) {
          setError(t("admin.productEditor.englishNameRequired"));
          return;
        }
        const created = await catalogApi.createProduct({
          sku,
          categoryId,
          status,
          attributes,
          translations,
        });
        navigate(`/admin/catalog/products/${created.id}`, { replace: true });
      } else if (productId) {
        await catalogApi.updateProduct(productId, {
          sku,
          ...(fromErp ? {} : { categoryId, status }),
          attributes,
          translations,
        });
        setSavingMsg(t("admin.productEditor.saved"));
      }
    } catch (err) {
      setError((err as ApiError).message);
    }
  }

  const flatCats = flatten(categories);

  return (
    <form onSubmit={handleSave} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, color: "var(--brand-ink)" }}>
            {isNew ? t("admin.productEditor.newProduct") : t("admin.productEditor.editProduct", { sku })}
          </h1>
          {!isNew && (createdAt || updatedAt) && (
            <div style={{ display: "flex", gap: 18, fontSize: 12, color: "var(--ink-3)", marginTop: 4 }}>
              {createdAt && <span>{t("admin.productEditor.created", "Created")}: {new Date(createdAt).toLocaleString(i18n.resolvedLanguage)}</span>}
              {updatedAt && <span>{t("admin.productEditor.lastEdited", "Last edited")}: {new Date(updatedAt).toLocaleString(i18n.resolvedLanguage)}</span>}
            </div>
          )}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button type="button" onClick={() => navigate(-1)} style={secondary}>{t("admin.productEditor.action.back")}</button>
          <button type="submit" style={primary}>{isNew ? t("admin.productEditor.action.create") : t("admin.productEditor.action.save")}</button>
        </div>
      </header>

      {error && <div style={errorBox}>{error}</div>}
      {savingMsg && <div style={successBox}>{savingMsg}</div>}

      <div style={tabs}>
        {(["general", "translations", "attributes", "assets"] as const).map((tk) => (
          <button
            key={tk}
            type="button"
            onClick={() => setTab(tk)}
            style={{ ...tabBtn, borderBottom: tab === tk ? "2px solid var(--brand-primary)" : "2px solid transparent" }}
          >
            {t(`admin.productEditor.tab.${tk}`)}
            {tk === "assets" && assets.length > 0 && <span style={chip}>{assets.length}</span>}
            {tk === "translations" && <span style={chip}>{Object.keys(translations).length}/8</span>}
          </button>
        ))}
      </div>

      {tab === "general" && (
        <section style={card}>
          <Field label={t("admin.productEditor.field.sku")} required>
            <input value={sku} onChange={(e) => setSku(e.target.value)} required style={input} />
          </Field>
          <Field label={t("admin.productEditor.field.name", "Name")}>
            {/* The name is per-locale (product_translation), so it is edited on
                the Translations tab — shown here read-only so the General tab
                is not a product without a name. */}
            <div style={{ ...input, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, background: "var(--surface-2)" }}>
              <span style={{ color: nameForLocale ? "var(--ink)" : "var(--ink-4)" }}>
                {nameForLocale || t("admin.productEditor.field.nameMissing", "Not set")}
              </span>
              <button type="button" className="btn sm" onClick={() => setTab("translations")}>
                {t("admin.productEditor.field.nameEdit", "Edit under Translations")}
              </button>
            </div>
          </Field>
          <Field label={t("admin.productEditor.field.category")} required={!fromErp}>
            {fromErp ? (
              <div data-testid="product-category-from-erp" style={readOnlyBox}>
                {categoryName(categories, categoryId) ?? "—"}
              </div>
            ) : (
              <Select<string>
                value={categoryId}
                onChange={setCategoryId}
                ariaLabel={t("admin.productEditor.field.category")}
                options={[
                  { value: "", label: t("admin.productEditor.field.categoryChoose") },
                  ...flatCats.map((c) => ({ value: c.id, label: c.name })),
                ]}
              />
            )}
          </Field>
          <Field label={t("admin.productEditor.field.status")}>
            {fromErp ? (
              <div data-testid="product-status-from-erp" style={readOnlyBox}>
                {t(`admin.products.status.${status}`)}
              </div>
            ) : (
              <Select<ProductStatus>
                value={status}
                onChange={setStatus}
                options={[
                  { value: "DRAFT", label: t("admin.products.status.DRAFT") },
                  { value: "PUBLISHED", label: t("admin.products.status.PUBLISHED") },
                  { value: "ARCHIVED", label: t("admin.products.status.ARCHIVED") },
                ]}
              />
            )}
          </Field>
          {fromErp && (
            <p style={{ color: "var(--ink-3)", fontSize: 12, margin: "6px 0 0" }}>
              {t("admin.productEditor.hint.statusFromErp", "The status and category of an article from the ERP follow the ERP, so they are not edited here.")}
            </p>
          )}
        </section>
      )}

      {tab === "translations" && (
        <section style={card}>
          {LOCALES.map((loc) => {
            const tr = translations[loc] ?? { name: "", shortDescription: null, longDescription: null };
            return (
              <div key={loc} style={{ borderBottom: "1px solid var(--border)", paddingBottom: 12, marginBottom: 12 }}>
                <strong style={{ textTransform: "uppercase", fontSize: 13 }}>{loc}</strong>
                <Field label={t("admin.productEditor.field.name")} required={loc === "en"}>
                  <input
                    value={tr.name}
                    onChange={(e) => setTranslations({ ...translations, [loc]: { ...tr, name: e.target.value } })}
                    required={loc === "en"}
                    style={input}
                  />
                </Field>
                <Field label={t("admin.productEditor.field.shortDescription")}>
                  <textarea
                    value={tr.shortDescription ?? ""}
                    onChange={(e) => setTranslations({ ...translations, [loc]: { ...tr, shortDescription: e.target.value } })}
                    rows={2}
                    style={{ ...input, fontFamily: "inherit", resize: "vertical" }}
                  />
                </Field>
                <Field label={t("admin.productEditor.field.longDescription")}>
                  <textarea
                    value={tr.longDescription ?? ""}
                    onChange={(e) => setTranslations({ ...translations, [loc]: { ...tr, longDescription: e.target.value } })}
                    rows={4}
                    style={{ ...input, fontFamily: "inherit", resize: "vertical" }}
                  />
                </Field>
              </div>
            );
          })}
        </section>
      )}

      {tab === "attributes" && (
        <section style={card}>
          {schema.length === 0 ? (
            <p style={{ color: "var(--ink-3)" }}>{t("admin.productEditor.attributes.empty")}</p>
          ) : (
            schema.map((entry) => (
              <Field
                key={entry.key}
                // labelKey lines up with the attr.* keys the catalog already
                // uses ("protein" -> "Proteini"), so the tab reads the same as
                // the product card instead of showing the raw key.
                label={`${attributeLabel(entry, i18n.resolvedLanguage ?? "en", t)}${entry.unit ? ` (${entry.unit})` : ""}`}
              >
                {entry.type === "select" && entry.options ? (
                  <Select<string>
                    value={String(attributes[entry.key] ?? "")}
                    onChange={(v) => setAttributes({ ...attributes, [entry.key]: v })}
                    options={[
                      { value: "", label: "—" },
                      ...entry.options.map((o) => ({
                        value: o,
                        label: optionLabel(entry, o, i18n.resolvedLanguage ?? "en", t),
                      })),
                    ]}
                  />
                ) : entry.type === "multiselect" && entry.options ? (
                  // Stored the way the catalog already reads it: one
                  // comma-joined string, not an array.
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                    {entry.options.map((o) => {
                      const picked = String(attributes[entry.key] ?? "")
                        .split(",").map((x) => x.trim()).filter(Boolean);
                      const on = picked.includes(o);
                      return (
                        <label key={o} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 13 }}>
                          <input
                            type="checkbox"
                            checked={on}
                            onChange={() => {
                              const next = on ? picked.filter((x) => x !== o) : [...picked, o];
                              setAttributes({ ...attributes, [entry.key]: next.join(",") });
                            }}
                          />
                          {optionLabel(entry, o, i18n.resolvedLanguage ?? "en", t)}
                        </label>
                      );
                    })}
                  </div>
                ) : entry.type === "boolean" ? (
                  <input
                    type="checkbox"
                    checked={!!attributes[entry.key]}
                    onChange={(e) => setAttributes({ ...attributes, [entry.key]: e.target.checked })}
                  />
                ) : (
                  <input
                    type={entry.type === "number" ? "number" : "text"}
                    value={String(attributes[entry.key] ?? "")}
                    onChange={(e) => setAttributes({
                      ...attributes,
                      [entry.key]: entry.type === "number" ? Number(e.target.value) : e.target.value,
                    })}
                    style={input}
                  />
                )}
              </Field>
            ))
          )}
          {/* Attributes are defined per category, which is not obvious while
              standing inside a product — so the way to add one is right here. */}
          <p style={{ marginTop: 14, marginBottom: 0, fontSize: 12.5, color: "var(--ink-3)" }}>
            {t("admin.productEditor.attributes.managedOnCategory",
               "Attributes are defined on the category, for every product in it.")}{" "}
            <Link to="/admin/catalog/categories" style={{ color: "var(--brand-primary)" }}>
              {t("admin.productEditor.attributes.manageLink", "Add or edit attributes")}
            </Link>
          </p>
        </section>
      )}

      {tab === "assets" && (
        productId ? (
          <AssetsPanel
            productId={productId}
            assets={assets}
            onChange={setAssets}
          />
        ) : (
          <section style={card}>
            <p style={{ color: "var(--ink-3)" }}>{t("admin.productEditor.assets.saveFirst")}</p>
          </section>
        )
      )}
    </form>
  );
}

function AssetsPanel({
  productId, assets, onChange,
}: {
  productId: string;
  assets: AssetView[];
  onChange: (next: AssetView[]) => void;
}) {
  const { t } = useTranslation();
  const { canFull } = useSectionAccess("catalog");
  const confirm = useConfirm();
  const [kind, setKind] = useState<AssetKind>("IMAGE");
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleFile(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null);
    setUploading(true);
    try {
      const created = await catalogApi.uploadAsset(productId, kind, file);
      onChange([...assets, created]);
    } catch (err) {
      setError((err as ApiError).message);
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  }

  async function remove(a: AssetView) {
    if (!(await confirm({ message: t("admin.productEditor.assets.confirmDelete", { filename: a.filename }), danger: true }))) return;
    await catalogApi.deleteAsset(productId, a.id);
    onChange(assets.filter((x) => x.id !== a.id));
  }

  async function move(a: AssetView, delta: number) {
    const updated = await catalogApi.reorderAsset(productId, a.id, Math.max(0, a.displayOrder + delta));
    onChange(assets.map((x) => (x.id === a.id ? updated : x)).sort((x, y) => x.displayOrder - y.displayOrder));
  }

  return (
    <section style={card}>
      <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
        <Select<AssetKind>
          value={kind}
          onChange={setKind}
          ariaLabel={t("admin.productEditor.assets.col.kind")}
          options={ASSET_KINDS.map((k) => ({ value: k, label: t(`catalog.assetKind.${k}`) }))}
          style={{ minWidth: 220 }}
        />
        <label style={{ ...primary, display: "inline-block", cursor: "pointer" }}>
          {uploading ? t("admin.productEditor.assets.uploading") : t("admin.productEditor.assets.chooseFile")}
          <input type="file" onChange={handleFile} disabled={uploading} style={{ display: "none" }} />
        </label>
        <span style={{ color: "var(--ink-3)", fontSize: 12 }}>{t("admin.productEditor.assets.maxSize")}</span>
      </div>
      {error && <div style={errorBox}>{error}</div>}
      {assets.length === 0 ? (
        <p style={{ color: "var(--ink-3)" }}>{t("admin.productEditor.assets.empty")}</p>
      ) : (
        <table style={tbl}>
          <thead>
            <tr>
              <th style={th}>{t("admin.productEditor.assets.col.order")}</th>
              <th style={th}>{t("admin.productEditor.assets.col.kind")}</th>
              <th style={th}>{t("admin.productEditor.assets.col.filename")}</th>
              <th style={th}>{t("admin.productEditor.assets.col.size")}</th>
              <th style={{ ...th, textAlign: "right" }}>{t("common.actions", "Actions")}</th>
            </tr>
          </thead>
          <tbody>
            {assets.map((a) => (
              <tr key={a.id}>
                <td style={td}>
                  <button type="button" onClick={() => move(a, -1)} style={iconBtn}>↑</button>
                  <button type="button" onClick={() => move(a, +1)} style={iconBtn}>↓</button>
                  <span style={{ marginLeft: 6 }}>{a.displayOrder}</span>
                </td>
                <td style={td}>{t(`catalog.assetKind.${a.kind}`, a.kind)}</td>
                <td style={td}>{a.filename}</td>
                <td style={td}>{(a.sizeBytes / 1024).toFixed(1)} KB</td>
                <td style={td}>
                  <button type="button" disabled={!canFull} onClick={() => remove(a)} style={dangerLink}>{t("admin.productEditor.action.delete")}</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function flatten(nodes: CategoryNode[], depth = 0): { id: string; name: string }[] {
  return nodes.flatMap((n) => [
    { id: n.id, name: `${"— ".repeat(depth)}${n.name}` },
    ...flatten(n.children, depth + 1),
  ]);
}

/** The name of a category anywhere in the tree, without the indent the picker gives it. */
function categoryName(nodes: CategoryNode[], id: string): string | null {
  for (const n of nodes) {
    if (n.id === id) return n.name;
    const below = categoryName(n.children ?? [], id);
    if (below) return below;
  }
  return null;
}

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 13, marginTop: 8 }}>
      <span style={{ color: "var(--ink-3)" }}>
        {label} {required && <span style={{ color: "var(--err)" }}>*</span>}
      </span>
      {children}
    </label>
  );
}

const readOnlyBox: React.CSSProperties = { padding: "8px 10px", borderRadius: 6, border: "1px solid var(--border-strong)", fontSize: 14, width: "100%", background: "var(--surface-2)", color: "var(--ink)" };
const card: React.CSSProperties = { background: "var(--surface)", padding: 20, borderRadius: 10, boxShadow: "var(--shadow-1)" };
const tabs: React.CSSProperties = { display: "flex", gap: 4, borderBottom: "1px solid var(--border)", marginBottom: -1 };
const tabBtn: React.CSSProperties = { padding: "10px 16px", background: "transparent", border: "none", borderBottom: "2px solid transparent", cursor: "pointer", fontSize: 14, color: "var(--ink)", display: "flex", alignItems: "center", gap: 6 };
const chip: React.CSSProperties = { background: "var(--surface-muted)", color: "var(--ink-3)", fontSize: 11, padding: "1px 6px", borderRadius: 999 };
const input: React.CSSProperties = { padding: "8px 10px", borderRadius: 6, border: "1px solid var(--border-strong)", fontSize: 14, width: "100%" };
const primary: React.CSSProperties = { padding: "8px 14px", background: "var(--brand-primary-600)", color: "var(--on-primary)", border: "none", borderRadius: 6, fontSize: 14, cursor: "pointer" };
const secondary: React.CSSProperties = { padding: "8px 14px", background: "var(--surface)", border: "1px solid var(--border-strong)", borderRadius: 6, fontSize: 14, cursor: "pointer" };
const dangerLink: React.CSSProperties = { background: "transparent", border: "none", color: "var(--err)", cursor: "pointer", fontSize: 13 };
const iconBtn: React.CSSProperties = { padding: "2px 8px", background: "var(--surface-2)", border: "1px solid var(--border-strong)", borderRadius: 4, fontSize: 12, cursor: "pointer", marginRight: 2 };
const tbl: React.CSSProperties = { width: "100%", borderCollapse: "collapse", fontSize: 13, marginTop: 12 };
const th: React.CSSProperties = { textAlign: "left", padding: "8px 10px", background: "var(--surface-2)", color: "var(--ink-3)", fontWeight: 600, borderBottom: "1px solid var(--border)" };
const td: React.CSSProperties = { padding: "8px 10px", borderBottom: "1px solid var(--border)" };
const errorBox: React.CSSProperties = { padding: 10, background: "var(--err-bg)", color: "var(--err)", borderRadius: 6, fontSize: 13 };
const successBox: React.CSSProperties = { padding: 10, background: "var(--ok-bg)", color: "var(--ok)", borderRadius: 6, fontSize: 13 };
